import {
  AssetInformationCardModule,
  CustomerInformationCardModule,
  DocumentCollectionDeadlineBanner,
  InvoicesModule,
  InvoicesScheduleModule,
  OnDemandDocumentGenerationCard,
  PageHeader,
  PaymentMethodModule,
  LogoModule,
  PolicyholderDashboardLayout,
  PolicyholderContactsModule,
  PolicyholderClaimsDeclarationModule,
  PolicyholderPaymentFailureBanner,
  QuoteTierModule,
  useAssertedParams,
  useToast,
  PolicyInformationCardModule,
  PolicyDocumentsSections,
  PolicyWarningBanner,
} from '@korint/folio';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bumpDocumentsRegenSignal } from '@folio/utils/documentsRegenSignal';
import { useDocumentConformityMap } from '@folio/hooks';
import {
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
  keyValueFieldsToObject,
  legacyDocumentsControllerGenerateDocumentOnDemand,
  policyControllerListPolicyDocumentsQueryOptions,
  uploadDocumentsOnPolicy,
} from 'korint-sdk';
import {
  useDocumentsAutoClassification,
  useFeatureFlag,
  useKorintUniqConfig,
  type PolicyholderPage,
} from 'korint-sdk/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';

export function UniqPolicyholderDashboardLayout({
  pages,
}: {
  pages: ReadonlyArray<PolicyholderPage>;
}) {
  return <PolicyholderDashboardLayout pages={pages} logo={<LogoModule className="h-8 w-auto" />} />;
}

export function PolicyholderHome() {
  return (
    <>
      <PolicyWarningBanner disableQuoteAction stopFunnelBasePath="/policyholder" />
      <PolicyholderContactsModule />
      <PolicyholderClaimsDeclarationModule />
    </>
  );
}

export function PolicyholderDashboardContentLayout({
  title,
  right,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { policyholderDashboard } = useKorintUniqConfig();

  return (
    <Stack>
      <PageHeader title={title} right={right} />
      {policyholderDashboard.showPaymentFailureBanner && <PolicyholderPaymentFailureBanner />}
      {children}
    </Stack>
  );
}

export function PolicyholderInformations() {
  return (
    <>
      <PolicyWarningBanner disableQuoteAction stopFunnelBasePath="/policyholder" />
      <QuoteTierModule showModifyButton={false} showCustomFields={false} variant="policyholder" />
      <CustomerInformationCardModule allowEdition={false} />
      <PolicyInformationCardModule allowEdition={false} variant="policyholder" />
      <AssetInformationCardModule allowEdition={false} />
    </>
  );
}

export function PolicyholderDocuments() {
  const { policyId, branchId } = useAssertedParams();
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { policy } = usePolicyOnDashboard();
  // Base branch's real id is the literal string "BASE" (see PolicyAggregateProjection).
  const conformityBranchId = branchId ?? 'BASE';

  const { data: policyDocuments, refetch: refetchPolicyDocuments } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId }),
    select: data =>
      data.data?.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const documentConformityFlagEnabled = useFeatureFlag(
    'feat_document_conformity',
    policy.productId,
  );

  const {
    data: conformityData,
    isSuccess: isDocumentReviewEnabled,
    refetch: refetchConformity,
  } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policyId, {
      branchId: conformityBranchId,
    }),
    retry: false,
    enabled: documentConformityFlagEnabled,
  });

  const { conformityByDocumentId } = useDocumentConformityMap(
    policyDocuments,
    conformityData?.conformities,
    isDocumentReviewEnabled,
  );

  const refetchReuploadedDocuments = useCallback(() => {
    refetchPolicyDocuments();
    refetchConformity();
  }, [refetchPolicyDocuments, refetchConformity]);

  const showReuploadError = useCallback(() => {
    toast.toast({
      title: t('documentsListsPolicy.conformity.reuploadError'),
      variant: 'destructive',
    });
  }, [toast, t]);

  // The S3 PUT alone leaves the document PENDING: only the validate stream confirms the upload.
  const { processDocuments } = useDocumentsAutoClassification({
    onDocumentValidated: refetchReuploadedDocuments,
    onDocumentError: showReuploadError,
    onAllComplete: refetchReuploadedDocuments,
  });

  const { mutate: reuploadDocument } = useMutation({
    mutationFn: async ({ documentId, file }: { documentId: string; file: File }) => {
      const rejectedDocument = policyDocuments?.find(document => document.id === documentId);
      const category = rejectedDocument?.category
        ? String(rejectedDocument.category)
        : 'uncategorized';
      return uploadDocumentsOnPolicy({
        policyId,
        branchId,
        payload: {
          file,
          type: 'supporting_document',
          category,
          metadata: { replacesdocumentid: documentId },
          name: file.name,
        },
      });
    },
    onSuccess: ({ key }) => {
      void processDocuments([key]);
    },
    onError: showReuploadError,
  });

  const { mutate: generateOnDemandDocument, isPending: isGeneratingDocument } = useMutation({
    mutationFn: (configName: string) =>
      legacyDocumentsControllerGenerateDocumentOnDemand({
        productId: policy.productId,
        configName,
        targetId: policyId,
      }),
    onSuccess: () => {
      refetchPolicyDocuments();
      // The document lands in the list a beat after the response; this polls until it shows.
      bumpDocumentsRegenSignal(queryClient, policy.id);
    },
  });

  return (
    <>
      <DocumentCollectionDeadlineBanner policyId={policyId} branchId={branchId} />
      <PolicyDocumentsSections
        displayContext="policyholder"
        conformityByDocumentId={conformityByDocumentId}
        onReuploadDocumentConformity={(documentId, file) => reuploadDocument({ documentId, file })}
      />
      <OnDemandDocumentGenerationCard
        onGenerate={configName => generateOnDemandDocument(configName)}
        isGenerating={isGeneratingDocument}
      />
    </>
  );
}

export function PolicyholderPaymentMethods() {
  return <PaymentMethodModule />;
}

export function PolicyholderInvoices() {
  return (
    <>
      <InvoicesModule />
      <InvoicesScheduleModule />
    </>
  );
}
